import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FullModal } from "../../modules/world/rmc/modals/fullModal";
import { CheveronRight, Search } from "../svg/GeneralIcons";
import { AnimatePresence } from "framer-motion";
import ReactCountryFlag from "react-country-flag";

const countries = [
  {
    name: "Nigeria",
    code: "NG",
    dialCode: "+234",
    flag: "🇳🇬",
  },
  {
    name: "United States",
    code: "US",
    dialCode: "+1",
    flag: "🇺🇸",
  },
  {
    name: "United Kingdom",
    code: "GB",
    dialCode: "+44",
    flag: "🇬🇧",
  },
  {
    name: "Canada",
    code: "CA",
    dialCode: "+1",
    flag: "🇨🇦",
  },
  {
    name: "Ghana",
    code: "GH",
    dialCode: "+233",
    flag: "🇬🇭",
  },
  {
    name: "South Africa",
    code: "ZA",
    dialCode: "+27",
    flag: "🇿🇦",
  },
  {
    name: "Kenya",
    code: "KE",
    dialCode: "+254",
    flag: "🇰🇪",
  },
  {
    name: "France",
    code: "FR",
    dialCode: "+33",
    flag: "🇫🇷",
  },
  {
    name: "Germany",
    code: "DE",
    dialCode: "+49",
    flag: "🇩🇪",
  },
  {
    name: "Australia",
    code: "AU",
    dialCode: "+61",
    flag: "🇦🇺",
  },
  {
    name: "India",
    code: "IN",
    dialCode: "+91",
    flag: "🇮🇳",
  },
];

export const PhoneInput = ({
  placeholder = "Enter phone number",
  label = "Phone Number",
  className,
  inputClass,
  value,
  onChange,
  ...props
}) => {
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempOverlay, setTempOverlay] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const sortedCountries = countries.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    if (value && !isInitialized) {
      let modifiedValue = "";

      if (typeof value === "string") {
        modifiedValue = value;
      } else if (typeof value === "object" && value.fullNumber) {
        modifiedValue = value.fullNumber;
      } else {
        return; // Invalid format
      }

      //runs when the page mounts, if there is a saved number from the BE else the input is left empty
      if (modifiedValue.startsWith("+")) {
        const country = sortedCountries.find((c) =>
          modifiedValue.startsWith(c.dialCode)
        );

        if (country) {
          setSelectedCountry(country);
          const phone = modifiedValue.replace(country.dialCode, "");
          setPhoneNumber(phone);
          setIsInitialized(true);
        }
      }
    }
  }, [value, isInitialized]);

  const filteredCountries = sortedCountries.filter((country) => {
    const query = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(query) ||
      country.dialCode.includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSearchQuery("");
    setTempOverlay(country.code); // Show overlay immediately

    setTimeout(() => {
      setTempOverlay(null);
      setIsModalOpen(false);
    }, 100);
  };

  const handlePhoneNumberChange = (e) => {
    const rawValue = e.target.value;

    const digitsOnly = rawValue.replace(/\D/g, "");
    setPhoneNumber(digitsOnly);
  };

  // Call onChange whenever country or phone number changes
  useEffect(() => {
    if (onChange && (phoneNumber || isInitialized)) {
      const formattedPhoneNumber = phoneNumber.startsWith("0")
        ? phoneNumber.slice(1)
        : phoneNumber;

      const completePhoneData = {
        country: selectedCountry,
        countryCode: selectedCountry.code,
        dialCode: selectedCountry.dialCode,
        phoneNumber: phoneNumber,
        fullNumber: `${selectedCountry.dialCode}${formattedPhoneNumber}`,
        formattedNumber: `${selectedCountry.dialCode} ${formattedPhoneNumber}`,
      };

      const syntheticEvent = {
        target: {
          value: completePhoneData,
        },
      };
      onChange(syntheticEvent);
    }
  }, [selectedCountry, phoneNumber, isInitialized]);

  const [selectorWidth, setSelectorWidth] = useState(100);
  const selectorRef = useRef(null);

  useEffect(() => {
    if (selectorRef.current) {
      setSelectorWidth(selectorRef.current.offsetWidth + 16);
    }
  }, [selectedCountry.dialCode]);

  const countrySelector = (
    <button
      ref={selectorRef}
      type="button"
      onClick={() => setIsModalOpen(true)}
      className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center px-2 hover:bg-gray-50 transition-colors z-10 border-r border-[#9ca3afc7]"
    >
      <ReactCountryFlag
        countryCode={selectedCountry.code}
        svg
        style={{ width: "1.5em", height: "1.5em" }}
      />
      <span className="text-[13px] font-medium text-gray-700 mx-1.5">
        {selectedCountry.dialCode}
      </span>
      <CheveronRight stroke="#6b7280" className="w-3 h-3 -rotate-90" />
    </button>
  );

  return (
    <div className={clsx("relative", className)}>
      <label className="text-sm text-primary-black mb-3.5 inline-block">
        {label}
      </label>
      <div className="relative">
        {countrySelector}
        <input
          type="tel"
          placeholder={placeholder}
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          className={clsx("base_input placeholder:text-sm", inputClass)}
          style={{ paddingLeft: `${selectorWidth}px` }}
          {...props}
        />
      </div>
      <AnimatePresence>
        {isModalOpen && (
          <FullModal
            className={clsx(
              "h-[500px] fixed inset-x-0 bottom-0 z-[4000] bg-[#F8F8F8]  480:max-w-[440px] 480:mx-auto rounded-t-[20px] overflow-hidden"
            )}
            onClose={() => {
              setIsModalOpen(false);
              setSearchQuery("");
            }}
          >
            <div className="py-5 px-6 bg-white h-full flex flex-col">
              <div
                onClick={() => {
                  setIsModalOpen(false);
                  setSearchQuery("");
                }}
                className="bg-[#4B5563] w-[50px] rounded-[5px] h-[6px] grid place-self-center mb-6 cursor-pointer"
              ></div>

              <div className=" mb-6">
                <h2 className="text-lg font-semibold text-center text-primary-black">
                  Select Country
                </h2>
                <div className="w-9"></div>
              </div>

              {/* Search input */}
              <div className="relative mb-4">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Search />
                </div>
                <input
                  type="text"
                  placeholder="Search country or code"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border-0 rounded-full text-base focus:ring-0  transition-all focus-visible:outline-none"
                />
              </div>

              <div className="flex-1 overflow-y-auto hidden-scrollbar">
                {filteredCountries.length > 0 ? (
                  <div className="space-y-1">
                    {filteredCountries.map((country, i) => {
                      const isLast = filteredCountries.length - 1 === i;

                      return (
                        <button
                          key={country.code}
                          onClick={() => handleCountrySelect(country)}
                          className={clsx(
                            !isLast && "border-b",
                            "w-full flex items-center justify-between px-4 py-3 transition-colors text-left  "
                          )}
                        >
                          <div className="flex gap-3 items-center  relative">
                            {tempOverlay === country.code && (
                              <div className="absolute inset-0 bg-white bg-opacity-80 rounded-lg transition-all duration-200 ease-in-out"></div>
                            )}
                            {/* <span className="text-2xl">{country.flag}</span> */}
                            <ReactCountryFlag
                              countryCode={country.code}
                              svg
                              style={{ width: "1.5em", height: "1.5em" }}
                            />
                            <div>
                              <div className="font-medium text-gray-900">
                                {country.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {country.dialCode}
                              </div>
                            </div>
                          </div>
                          {selectedCountry.code === country.code && (
                            <svg
                              className="w-5 h-5 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="text-gray-400 mb-2">
                      <svg
                        className="w-10 h-10 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-semibold text-base">
                      No countries found
                    </p>
                    <p className="text-gray-400 font-medium text-xs mt-1">
                      Try a different search term
                    </p>
                  </div>
                )}
              </div>
            </div>
          </FullModal>
        )}
      </AnimatePresence>
    </div>
  );
};
